import { ErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { RouteErrorCard } from "@client/components/utils/RouteErrorCard";
import type { ReactNode } from "react";
import { useLocation, useSearchParams } from "wouter";

/**
 * Catches a render crash inside a routed page and shows `RouteErrorCard` in
 * its place, keeping the app shell (app bar, drawers) alive. Without this the
 * nearest boundary is the root, and React unmounts the whole app to a blank
 * page. Navigating to another page clears the error; healthy pages are left
 * alone (no remount), so in-page state survives route param changes.
 *
 * The reset key includes the query string, not just the pathname: several
 * pages (Comparisons' `?mvf=` mode, the funder workspace's `?proposal=` modal)
 * key their render on search params, so Back/Forward to a healthy state can
 * change only the query — and the error card must clear on that too.
 */
export function RouteErrorBoundary({
  homeHref,
  homeLabel,
  children,
}: {
  homeHref: string;
  homeLabel: string;
  children: ReactNode;
}) {
  const [location] = useLocation();
  const [searchParams] = useSearchParams();
  return (
    <ErrorBoundary
      resetOnChange={`${location}?${searchParams.toString()}`}
      fallback={({ error }) => (
        <RouteErrorCard
          error={error}
          homeHref={homeHref}
          homeLabel={homeLabel}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
